import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnInit,
  Signal,
  signal,
  viewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { DropdownItem } from '@shared/abstraction';
import { parseTo } from '@shared/helpers/utils';
import { mapDropdownFromClient } from '@shared/mappers';
import { Client } from '@shared/models';
import {
  CurrentUserService,
  HeaderService,
  ModalService,
} from '@shared/services';
import {
  Collection,
  InvestmentJurisdiction,
} from 'src/app/modules/admin/models';
import { InvestmentJurisdictionService } from 'src/app/modules/admin/services';
import { ClientSelectionModalComponent } from '../client-selection-modal/client-selection-modal.component';
import { ComboboxComponent } from '../combobox/combobox.component';

@Component({
  standalone: false,
  selector: 'gstar-client-selection',
  templateUrl: './client-selection.component.html',
  styleUrls: ['./client-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientSelectionComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly headerService = inject(HeaderService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly modalService = inject(ModalService);
  private readonly currentUserService = inject(CurrentUserService);

  isAnyClient: Signal<boolean> = computed(
    () => this.headerService.clientList().length > 0
  );
  isSingleClient: Signal<boolean> = computed(
    () => this.headerService.clientList().length === 1
  );
  isDisabled = computed<boolean>(() => {
    return this.headerService.isDisabled();
  });
  isFirstLoad = signal(true);

  clientList = signal<DropdownItem[]>([]);

  selectedClient: Signal<DropdownItem | undefined> = computed(() => {
    const client = this.headerService.selectedClient();
    if (!client) return undefined;
    return parseTo<DropdownItem>({
      label: client!.name,
      value: client!.id,
    });
  });

  clientCombobox = viewChild<ComboboxComponent<Client>>('clientCombobox');

  constructor() {
    this.headerService.clientList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((clients: Client[]) => {
        this.clientList.set(mapDropdownFromClient(clients));
        if (this.isSingleClient()) {
          this.headerService.setClientById({
            clientId: clients.at(0)?.id,
            isManuallySelected: true,
          });
          this.headerService.updatedClient.set(true);
        }

        if (
          this.headerService.selectedClient() === undefined &&
          this.headerService.clientList().length === 0
        ) {
          this.openClientModal();
        } else {
          this.checkCountryMaterials();
        }
      });

    this.headerService.clearSelection$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clientCombobox()?.clear();
      });
  }

  ngOnInit(): void {
    this.headerService.reloadClientList();
  }

  checkCountryMaterials(): void {
    this.investmentJurisdictionService
      .activeInvestmentJurisdictionList()
      .subscribe((items: Collection<InvestmentJurisdiction>) => {
        if (items.items.length === 0) {
          this.openSelectedClientModal();
        } else {
          this.isFirstLoad.set(false);
        }
      });
  }

  selectItem(event: DropdownItem | null | undefined): void {
    this.headerService.updatedClient.set(true);
    if (event) {
      const selectedClientId = <number>event!.value;
      this.headerService.setClientById({
        clientId: selectedClientId,
        isManuallySelected: true,
      });
    }
    this.checkCountryMaterials();
  }

  clearSelection(): void {
    this.headerService.clearSelection();
  }

  closeModal(): void {
    this.authService.logout().subscribe(() => this.router.navigate(['/login']));
  }

  closeSelectionModal(): void {
    this.headerService.updatedClient.set(true);
    this.headerService.clear();
    this.headerService.reloadClientList();
  }

  private openClientModal(): void {
    const modalRef = this.modalService.open(
      ClientSelectionModalComponent,
      {
        appModalId: 'client-modal',
      },
      {
        title: signal('dashboard.noClientAssignedModalTitle'),
        description: signal('dashboard.noClientAssignedModalMessage'),
      }
    );

    modalRef.instance.afterClosed().subscribe(() => this.closeModal());
  }

  private openSelectedClientModal(): void {
    const modalRef = this.modalService.open(
      ClientSelectionModalComponent,
      {
        appModalId: 'selected-client-modal',
      },
      {
        title: signal('dashboard.noCountryAssignedModalTitle'),
        description: signal('dashboard.noCountryAssignedModalMessage'),
      }
    );

    modalRef.instance.afterClosed().subscribe(() => {
      if (this.isFirstLoad() && !this.currentUserService.isAdmin()) {
        this.closeModal();
      } else {
        this.closeSelectionModal();
      }
      this.isFirstLoad.set(false);
    });
  }
}
